import React, { useState, useEffect } from "react";
import styles from "./invoice-create-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Dropdown from "../../ui/dropdown";
import Button from "../../ui/button";
import Table from "../../ui/table";
import BudgetLineEditModal from "../budget-line-edit-modal";
import { createFactura, createPrefactura, editPrefactura, getLocalities, getBudgetLinesForInvoice, changePresupuestoIva, getPrefacturaById } from "../../../services/apiService";
import { showSuccessToast, showErrorToast } from "../../../utils/notifications";
import { getTodayISO } from "../../../utils/dateUtils";

const InvoiceCreateModal = ({ isOpen, onClose, presupuestoData, onSuccess }) => {
  const editingPrefacturaId = presupuestoData?.editingPrefacturaId || null;
  const isEditMode = !!editingPrefacturaId;
  console.log("InvoiceCreateModal opened with presupuestoData:", presupuestoData);
  // State for budget line edit modal
  const [isBudgetLineModalOpen, setIsBudgetLineModalOpen] = useState(false);
  const [selectedBudgetLineId, setSelectedBudgetLineId] = useState(null);

  const [formData, setFormData] = useState({
    numero: "",
    fecha: getTodayISO(), // Today's date in YYYY-MM-DD format
    tipoCobro: { label: "Transferencia", value: "Transferencia" }, // Preseleccionado
    iva: null,
    costePresupuesto: "",
    costeFactura: "",
    porcentaje: "",
    clientType: { label: "Cliente", value: "cliente" }, // Preseleccionado
    nombre: "",
    domicilio: "",
    localidad: null, // Cambiado a null para dropdown
    nif: "",
  });

  const [budgetData, setBudgetData] = useState(null);
  const [loadingBudgetData, setLoadingBudgetData] = useState(false);

  const [tipoCobroOptions] = useState([
    { label: "Transferencia", value: "Transferencia" },
    { label: "Metalico", value: "Metalico" },
    { label: "Bizum", value: "Bizum" },
    { label: "Giro", value: "Giro" },
  ]);

  const [ivaOptions] = useState([
    { label: "21%", value:"21%" },
    { label: "10%", value: "10%" },
  ]);

  const [clientTypeOptions] = useState([
    { label: "Cliente", value: "cliente" },
    { label: "Compañía", value: "compania" },
  ]);

  const [localityOptions, setLocalityOptions] = useState([]);

  useEffect(() => {
    if (presupuestoData) {
      setFormData(prev => ({
        ...prev,
        numero: String(presupuestoData.numero || ""),
        costePresupuesto: String(presupuestoData.presupuestoTotal || ""),
      }));
    }
  }, [presupuestoData]);

  useEffect(() => {
    // Cargar localidades cuando se abra el modal
    const fetchLocalities = async () => {
      try {
        const localities = await getLocalities();
        const formattedLocalities = localities.map(locality => ({
          label: `${locality.municipio} (${locality.codigoPostal})`,
          value: locality.id
        }));
        setLocalityOptions(formattedLocalities);
      } catch (error) {
        console.error("Error fetching localities:", error);
      }
    };

    if (isOpen) {
      fetchLocalities();
    }
  }, [isOpen]);

  useEffect(() => {
    // Cargar las líneas de presupuesto cuando se abra el modal
    const fetchBudgetLines = async () => {
      if (!presupuestoData?.presupuestoId) return;
      
      try {
        setLoadingBudgetData(true);
        const data = await getBudgetLinesForInvoice(presupuestoData.presupuestoId);
        console.log("Budget lines fetched:", data);
        setBudgetData(data);
        
        // Pre-rellenar los campos de cliente/compañía según lo que venga en el budget
        if (data) {
          const ivaValue = data.iva ? { label: data.iva, value: data.iva } : null;
          console.log("ivaValue que se va a establecer:", ivaValue);
          console.log("Budget data recibido:", data);
          
          setFormData(prev => {
            console.log("formData anterior (prev.iva):", prev.iva);
            const newFormData = {
              ...prev,
              costePresupuesto: String(data.presupuestoTotal || ""),
              nombre: data.nombreCliente || "",
              domicilio: data.domcilioCliente || "",
              localidad: data.localidadCliente ? {
                label: `${data.localidadCliente.municipio} (${data.localidadCliente.codigoPostal})`,
                value: data.localidadCliente.id
              } : null,
              nif: data.nifCliente || "",
              // Set IVA from budget data if available
              iva: ivaValue
            };
            console.log("nuevo formData.iva:", newFormData.iva);
            return newFormData;
          });
        }
      } catch (error) {
        console.error("Error fetching budget lines:", error);
        showErrorToast("Error al cargar las líneas de presupuesto");
      } finally {
        setLoadingBudgetData(false);
      }
    };

    if (isOpen && presupuestoData) {
      fetchBudgetLines();
    }
  }, [isOpen, presupuestoData]);

  // Cuando se está editando/visualizando una prefactura, cargar sus datos y mapear al formulario
  useEffect(() => {
    if (!isOpen || !editingPrefacturaId) return;

    const fetchPrefactura = async () => {
      try {
        const pref = await getPrefacturaById(editingPrefacturaId);
        console.log("Prefactura loaded:", pref);

        setFormData(prev => {
          return {
            ...prev,
            tipoCobro: pref.tipoCobro ? { label: pref.tipoCobro, value: pref.tipoCobro } : prev.tipoCobro,
            costeFactura: pref.costeFinal != null ? String(pref.costeFinal) : prev.costeFactura,
            porcentaje: pref.porcentage != null ? String(pref.porcentage) : prev.porcentaje,
          };
        });
      } catch (error) {
        console.error("Error fetching prefactura:", error);
        showErrorToast("Error al cargar la prefactura");
      }
    };

    fetchPrefactura();
  }, [isOpen, editingPrefacturaId, localityOptions]);

  // Function to reload budget lines after editing
  const reloadBudgetLines = async () => {
    if (!presupuestoData?.presupuestoId) return;
    
    try {
      setLoadingBudgetData(true);
      const data = await getBudgetLinesForInvoice(presupuestoData.presupuestoId);
      setBudgetData(data);
    } catch (error) {
      console.error("Error reloading budget lines:", error);
      showErrorToast("Error al recargar las líneas de presupuesto");
    } finally {
      setLoadingBudgetData(false);
    }
  };

  // Handle opening budget line edit modal
  const handleEditBudgetLine = (line) => {
    console.log("Opening edit modal for line:", line);
    setSelectedBudgetLineId(line.lineaId);
    // Pequeño delay para evitar conflictos con el evento mousedown
    setTimeout(() => {
      setIsBudgetLineModalOpen(true);
    }, 0);
  };

  // Handle closing budget line edit modal
  const handleCloseBudgetLineModal = () => {
    setIsBudgetLineModalOpen(false);
    setSelectedBudgetLineId(null);
  };

  // Handle budget line update
  const handleBudgetLineUpdate = async () => {
    await reloadBudgetLines();
  };

  const handleInputChange = (field, value) => {
    // Ensure value is always a string
    const stringValue = typeof value === 'string' ? value : String(value || '');
    
    // Si es el campo de porcentaje, limitar a 2 decimales
    if (field === "porcentaje" && stringValue) {
      const numValue = parseFloat(stringValue);
      if (!isNaN(numValue)) {
        const roundedValue = Math.round(numValue * 100) / 100;
        // Solo actualizar si el valor redondeado es diferente
        if (Math.abs(numValue - roundedValue) > 0.001) {
          value = String(roundedValue);
        }
      }
    }
    
    setFormData(prev => {
      const newFormData = { ...prev, [field]: stringValue };

      // Si se cambia Total factura, calcular automáticamente el porcentaje
      if (field === "costeFactura" && budgetData) {
        const facturaTotal = parseFloat(stringValue) || 0;
        const presupuestoTotal = budgetData.presupuestoTotal || 0;
        const presupuestoFacturado = budgetData.presupuestoFacturado || 0;
        const porcentagePresupuestoTotal = (budgetData.porcentagePresupuestoTotal || 0) / 100; // Convertir a decimal

        if (presupuestoTotal > 0) {
          // porcentaje = Math.max(0, Math.min((FacturaTotal + presupuestoFacturado) / PresupuestoTotal - porcentagePresupuestoTotal, 1 - porcentagePresupuestoTotal))
          const porcentajeCalculado = Math.max(
            0,
            Math.min(
              (facturaTotal + presupuestoFacturado) / presupuestoTotal - porcentagePresupuestoTotal,
              1 - porcentagePresupuestoTotal
            )
          );
          newFormData.porcentaje = String((porcentajeCalculado * 100).toFixed(2)); // Convertir a porcentaje
        }
      }

      // Si se cambia Porcentaje, calcular automáticamente el total factura
      if (field === "porcentaje" && budgetData) {
        const porcentaje = parseFloat(stringValue) || 0;
        const presupuestoTotal = budgetData.presupuestoTotal || 0;
        const presupuestoFacturado = budgetData.presupuestoFacturado || 0;
        const porcentagePresupuestoTotal = (budgetData.porcentagePresupuestoTotal || 0) / 100; // Convertir a decimal

        if (presupuestoTotal > 0) {
          // FacturaTotal = max(0, PresupuestoTotal * min(1, porcentagePresupuestoTotal + porcentaje) - presupuestoFacturado)
          const facturaCalculada = Math.max(
            0,
            presupuestoTotal * Math.min(1, porcentagePresupuestoTotal + porcentaje / 100) - presupuestoFacturado
          );
          newFormData.costeFactura = String(facturaCalculada.toFixed(2));
        }
      }

      return newFormData;
    });
  };

  const handleDropdownChange = (field, selectedOption) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [field]: selectedOption
      };

      // Si se cambia el tipo de cliente/compañía, actualizar los campos automáticamente
      if (field === "clientType" && budgetData) {
        if (selectedOption.value === "cliente") {
          newFormData.nombre = budgetData.nombreCliente || "";
          newFormData.domicilio = budgetData.domcilioCliente || "";
          newFormData.localidad = budgetData.localidadCliente ? {
            label: `${budgetData.localidadCliente.municipio} (${budgetData.localidadCliente.codigoPostal})`,
            value: budgetData.localidadCliente.id
          } : null;
          newFormData.nif = budgetData.nifCliente || "";
        } else if (selectedOption.value === "compania") {
          newFormData.nombre = budgetData.nombreCompania || "";
          newFormData.domicilio = budgetData.domcilioCompania || "";
          newFormData.localidad = budgetData.localidadCompania ? {
            label: `${budgetData.localidadCompania.municipio} (${budgetData.localidadCompania.codigoPostal})`,
            value: budgetData.localidadCompania.id
          } : null;
          newFormData.nif = budgetData.nifCompania || "";
        }
      }

      return newFormData;
    });
  };

  const isFormValidForFactura = () => {
    return formData.tipoCobro && 
           formData.iva !== null && 
           formData.costeFactura && 
           formData.clientType &&
           formData.nombre && 
           formData.domicilio && 
           formData.localidad && // Ya es un objeto con .value
           formData.nif;
  };

  const isFormValidForPrefactura = () => {
    return formData.porcentaje && 
           parseFloat(formData.porcentaje) > 0 &&
           formData.costeFactura && 
           parseFloat(formData.costeFactura) > 0;
  };

  const handleSave = async () => {
    try {
      // Si el IVA ha cambiado, actualizar primero en el presupuesto
      const presupuestoId = presupuestoData?.presupuestoId || budgetData?.presupuestoId;
      if (presupuestoId && formData.iva && budgetData?.iva !== formData.iva.value) {
        try {
          console.log("[LOG] Cambiando IVA del presupuesto:", { presupuestoId, nuevoIva: formData.iva.value, ivaAnterior: budgetData?.iva });
          await changePresupuestoIva(presupuestoId, formData.iva.value);
          console.log("[LOG] IVA cambiado exitosamente");
        } catch (error) {
          console.error("Error al cambiar el IVA:", error);
          showErrorToast("Error al actualizar el IVA");
          return;
        }
      }

      // Preparar los datos para enviar a la API
      const porcentaje = parseFloat(formData.porcentaje) || 0;
      const apiData = {
        numFactura: formData.numero || "",
        expedienteId: presupuestoData?.expedienteId || null,
        fecha: formData.fecha,
        presupuestoId: presupuestoData?.presupuestoId,
        tipoCobro: formData.tipoCobro?.label || formData.tipoCobro?.value || formData.tipoCobro,
        costeTotal: parseFloat(formData.costePresupuesto) || 0,
        porcentage: porcentaje,
        costeFinal: parseFloat(formData.costeFactura) || 0,
        porcentageFinal: porcentaje,
        destinatarioFactura: formData.clientType?.label || "Cliente",
        nombreFacturacion: formData.nombre,
        domicilioFacturacion: formData.domicilio,
        localidadFacturacionId: formData.localidad?.value || null,
        nifFacturacion: formData.nif
      };

      console.log("[LOG] Guardando prefactura:", { prefacturaId: editingPrefacturaId, body: apiData });

      await editPrefactura(editingPrefacturaId, apiData);
      showSuccessToast("Pre-factura guardada exitosamente");

      // Call onSuccess callback if provided (prop takes precedence)
      try {
        if (onSuccess) await onSuccess();
        else if (presupuestoData?.onSuccess) await presupuestoData.onSuccess();
      } catch (e) {
        // ignore callback errors
        console.error('onSuccess callback error:', e);
      }

      onClose();
    } catch (error) {
      console.error("Error saving prefactura:", error);
      const apiMessage = error?.response?.data?.detail || error?.response?.data?.message || error?.message;
      showErrorToast(apiMessage || "Error al guardar pre-factura");
    }
  };

  const handleCreate = async (type) => {
    try {
      // Si el IVA ha cambiado, actualizar primero en el presupuesto
      const presupuestoId = presupuestoData?.presupuestoId || budgetData?.presupuestoId;
      if (presupuestoId && formData.iva && budgetData?.iva !== formData.iva.value) {
        try {
          console.log("[LOG] Cambiando IVA del presupuesto antes de crear:", { presupuestoId, nuevoIva: formData.iva.value, ivaAnterior: budgetData?.iva });
          await changePresupuestoIva(presupuestoId, formData.iva.value);
          console.log("[LOG] IVA cambiado exitosamente");
        } catch (error) {
          console.error("Error al cambiar el IVA:", error);
          showErrorToast("Error al actualizar el IVA");
          return;
        }
      }

      // Preparar los datos para enviar a la API según el formato esperado
      const apiData = {
        expedienteId: presupuestoData?.expedienteId || null, // Necesitamos el expedienteId
        fecha: formData.fecha,
        presupuestoId: presupuestoData?.presupuestoId,
        tipoCobro: formData.tipoCobro?.label || formData.tipoCobro?.value, // Usar label como en el ejemplo
        iva: formData.iva?.value || formData.iva, // Agregar IVA
        costeTotal: parseFloat(formData.costePresupuesto) || 0,
        porcentage: parseFloat(formData.porcentaje) || 0, // Nota: "porcentage" sin 'a' como en el ejemplo
        costeFinal: parseFloat(formData.costeFactura) || 0,
        destinatarioFactura: formData.clientType?.label || "Cliente", // "Cliente" o "Compañia"
        nombreFacturacion: formData.nombre,
        domicilioFacturacion: formData.domicilio,
        localidadFacturacionId: formData.localidad?.value || null, // ID de la localidad seleccionada
        nifFacturacion: formData.nif
      };

      // LOG: mostrar en consola los datos que serán enviados a la API
      try {
        console.log("[LOG] Enviando petición de creación:", { action: type, endpoint: type === 'factura' ? '/factura/crear' : '/prefactura/crear', body: apiData });
      } catch (logErr) {
        // Evitar romper la UI si fallara el stringify u otro
        console.log("[LOG] Enviando petición de creación (no se pudo serializar body)", type);
      }

      // Llamadas específicas a la API según el tipo
      if (type === "factura") {
        await createFactura(apiData);
        showSuccessToast(`Factura creada exitosamente`);
      } else if (type === "prefactura") {
        await createPrefactura(apiData);
        showSuccessToast(`Pre-factura creada exitosamente`);
      }

      // Llamar al callback onSuccess si existe (prop precedence)
      try {
        if (onSuccess) await onSuccess();
        else if (presupuestoData?.onSuccess) await presupuestoData.onSuccess();
      } catch (e) {
        console.error('onSuccess callback error:', e);
      }

      // Fallback global: si se creó una factura real, emitir un evento para forzar refetch en el padre
      try {
        if (type === 'factura' && typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('AGER:REFETCH_RECORD'));
        }
      } catch (e) {
        // no-op
      }

      onClose();
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
      // Prefer API-provided message when available (extract detail or message from error response)
      const apiMessage = error?.response?.data?.detail || error?.response?.data?.message || error?.message;
      showErrorToast(apiMessage || `Error al crear ${type === "factura" ? "factura" : "pre-factura"}`);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>Editar Pre-Factura / Crear factura</h2>
            <button className={styles.closeButton} onClick={onClose}>
              <MyIcons name="close" size={24} color="black" />
            </button>
          </div>

        <div className={styles.modalBody}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>General</h3>
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>Núm. presupuesto</label>
                <Input
                  type="text"
                  value={formData.numero}
                  onChange={(e) => handleInputChange("numero", e.target.value)}
                  placeholder="5049"
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Fecha</label>
                <Input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => handleInputChange("fecha", e.target.value)}
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Tipo de cobro</label>
                <Dropdown
                  options={tipoCobroOptions}
                  selected={formData.tipoCobro}
                  onSelect={(option) => handleDropdownChange("tipoCobro", option)}
                  placeholder="Seleccionar"
                  style={{ width: '100%' }}
                  mainStyle={{ width: '100%' }}
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>IVA</label>
                <Dropdown
                  options={ivaOptions}
                  selected={formData.iva}
                  onSelect={(option) => handleDropdownChange("iva", option)}
                  placeholder="Seleccionar"
                  style={{ width: '100%' }}
                  mainStyle={{ width: '100%' }}
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Total presupuesto</label>
                <Input
                  type="text"
                  value={formData.costePresupuesto}
                  onChange={(e) => handleInputChange("costePresupuesto", e.target.value)}
                  placeholder="Precio"
                  disabled={true}
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Total factura</label>
                <Input
                  type="text"
                  value={formData.costeFactura}
                  onChange={(e) => handleInputChange("costeFactura", e.target.value)}
                  placeholder="Precio"
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Porcentaje</label>
                <Input
                  type="text"
                  value={formData.porcentaje}
                  onChange={(e) => handleInputChange("porcentaje", e.target.value)}
                  placeholder="%"
                />
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.clientHeader}>
              <Dropdown
                options={clientTypeOptions}
                selected={formData.clientType}
                onSelect={(option) => handleDropdownChange("clientType", option)}
                clearable={false}
              />
            </div>
            
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>Nombre</label>
                <Input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => handleInputChange("nombre", e.target.value)}
                  placeholder="Nombre"
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Domicilio</label>
                <Input
                  type="text"
                  value={formData.domicilio}
                  onChange={(e) => handleInputChange("domicilio", e.target.value)}
                  placeholder="Domicilio"
                />
              </div>
              <div className={styles.inputGroup}>
                <label>Localidad</label>
                <Dropdown
                  options={localityOptions}
                  selected={formData.localidad}
                  onSelect={(option) => handleDropdownChange("localidad", option)}
                  placeholder="Seleccionar localidad"
                  style={{ width: '100%' }}
                  mainStyle={{ width: '100%' }}
                />
              </div>
              <div className={styles.inputGroup}>
                <label>NIF</label>
                <Input
                  type="text"
                  value={formData.nif}
                  onChange={(e) => handleInputChange("nif", e.target.value)}
                  placeholder="Número NIF"
                />
              </div>
            </div>
          </div>

          <h3 className={styles.sectionTitle}>Líneas de Presupuesto</h3>
          {loadingBudgetData ? (
            <div className={styles.loadingContainer}>
              <p>Cargando líneas de presupuesto...</p>
            </div>
          ) : budgetData?.lineas && budgetData.lineas.length > 0 ? (
            <div>
              <Table
              headers={[
                { key: "gremio", label: "Gremio" },
                { key: "presupuestoTotal", label: "Presupuesto Total" },
                { key: "costeTotal", label: "Coste Total" },
                { key: "conjuntoTotal", label: "R. Conjunto" },
                { key: "beneficioTotal", label: "Beneficio" },
                { key: "realizado", label: "Realizado" },
                { key: "noRealizar", label: "No Realizar" },
              ]}
              data={budgetData.lineas
                .filter(line => !line.noRealizar)  // Filtrar ANTES del map, cuando noRealizar es booleano
                .map((line, index) => ({
                  ...line,
                  id: index,
                  lineaId: line.id,
                  presupuestoTotal: `${line.presupuestoTotal}€`,
                  costeTotal: `${line.costeTotal}€`,
                  conjuntoTotal: `${(line.conjuntoTotal * 100).toFixed(2)}%`,
                  beneficioTotal: `${line.beneficioTotal}€`,
                  realizado: line.realizado ? "Sí" : "No",
                  noRealizar: line.noRealizar ? "Sí" : "No",
                }))}
              renderActions={(row) => (
                <button
                  className={styles.editButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditBudgetLine(row);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  title="Editar línea"
                >
                  <MyIcons name="eye" width={20} height={20} color={"var(--main)"} />
                </button>
              )}
              />
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No hay líneas de presupuesto disponibles</p>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          {isEditMode ? (
            <>
              <Button
                variant="secondary"
                onClick={handleSave}
                className={styles.createButton}
              >
                Guardar
              </Button>
              <Button
                variant="primary"
                onClick={() => handleCreate("factura")}
                disabled={!isFormValidForFactura()}
                className={styles.createButton}
              >
                Crear Factura
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => handleCreate("prefactura")}
                disabled={!isFormValidForPrefactura()}
                className={styles.createButton}
              >
                Crear Pre-factura
              </Button>
              <Button
                variant="primary"
                onClick={() => handleCreate("factura")}
                disabled={!isFormValidForFactura()}
                className={styles.createButton}
              >
                Crear Factura
              </Button>
            </>
          )}
        </div>
      </div>
    </div>

    {/* Budget Line Edit Modal - Renderizado fuera del modal principal */}
    {isBudgetLineModalOpen && selectedBudgetLineId && (
      <BudgetLineEditModal
        isOpen={isBudgetLineModalOpen}
        onClose={handleCloseBudgetLineModal}
        lineaId={selectedBudgetLineId}
        expedienteId={presupuestoData?.expedienteId}
        onUpdate={handleBudgetLineUpdate}
      />
    )}
  </>
  );
};

export default InvoiceCreateModal;
